import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { organisationApi } from "../api/organisationApi";
import { withoutPermissionToast } from "../utils/permissionError";
import { toast } from "../components/common/toast";
import { useAuth } from "./AuthProvider";

// /group/search requires the Group:Search permission, which narrow roles
// (e.g. a Contract-only "Editor") don't hold — yet every logged-in user
// hits this provider just to resolve their own org's name/module flags for
// the top bar. That 403 is an expected, already-handled outcome (both
// effects below leave their state as null and move on), not a bug to
// surface as a console error on every page load for those roles.
const isForbidden = (err: unknown): boolean =>
  (err as { response?: { status?: number } })?.response?.status === 403;

export interface CurrentOrganisation {
  id: string;
  name: string;
}


interface OrganisationSwitchContextType {
  currentOrganisation: CurrentOrganisation | null;
  /**
   * The organisation's configured IANA time zone, or null until its record has
   * loaded. Everything date-shaped is resolved against this rather than the
   * browser's zone: a contract expires on the date the organisation operates
   * on, and two people opening it from different countries must agree on it.
   */
  organisationTimeZone: string | null;
  switching: boolean;
  switchOrganisation: (
    organisationId: string,
    organisationName: string,
  ) => Promise<void>;
}

const OrganisationSwitchContext =
  createContext<OrganisationSwitchContextType | null>(null);

const readStoredOrganisation = (): CurrentOrganisation | null => {
  const id = localStorage.getItem("organisation");
  const name = localStorage.getItem("companyName");
  return id && name ? { id, name } : null;
};

export const OrganisationSwitchProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuth();
  const { t } = useTranslation("topbar");
  // Used only for an instant, no-flash first paint (e.g. on a hard reload).
  // The effect below is the actual source of truth once the user profile is
  // available — it doesn't depend on some other component (Login.tsx) having
  // written localStorage by a particular point in time.
  const [currentOrganisation, setCurrentOrganisation] =
    useState<CurrentOrganisation | null>(readStoredOrganisation);
  const [organisationTimeZone, setOrganisationTimeZone] = useState<
    string | null
  >(null);
  const [switching, setSwitching] = useState(false);

  // Bootstrap the current organisation from the authenticated user's own
  // profile (companyId, returned by /Auth/me) ONLY when nothing is known
  // yet (fresh login, empty localStorage) — this avoids the race with
  // Login.tsx's localStorage write that used to leave the switcher stuck on
  // "Select Organisation" until a reload.
  //
  // Once currentOrganisation has a value, this effect must NOT re-derive
  // from user.companyId again: /Auth/me's CompanyId reflects the member's
  // own home organisation from the database, not the organisation the
  // current JWT was scoped to by a switch — a backend characteristic, not
  // something fixable here. Treating companyId as authoritative after a
  // switch would silently revert the selection back to the Super Admin's
  // home organisation on every reload, which is the exact bug this guard
  // prevents. switchOrganisation() below is the only thing allowed to
  // change currentOrganisation once it's set.
  useEffect(() => {
    if (currentOrganisation) return;
    const companyId = user?.companyId;
    if (!companyId) return;

    let cancelled = false;

    const load = async () => {
      try {
        const res = await withoutPermissionToast(() =>
          organisationApi.searchOrganisations({
            ids: [companyId],
            pageSize: 1,
            pageIndex: 0,
          }),
        );
        if (cancelled) return;

        const org = (res?.data || [])[0];
        const name: string | undefined = org?.title || org?.name;
        if (!name) return;

        localStorage.setItem("organisation", companyId);
        localStorage.setItem("companyName", name);
        setCurrentOrganisation({ id: companyId, name });
      } catch (err) {
        if (!isForbidden(err)) {
          console.error("Failed to resolve current organisation:", err);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [user?.companyId, currentOrganisation]);

  // Module-enablement flags aren't persisted in localStorage (only id/name
  // are), so this re-fetches the current organisation's full record whenever
  // its id becomes known/changes — including on a plain reload, where the
  // effect above skips its own fetch because currentOrganisation is already
  // bootstrapped from localStorage.
  useEffect(() => {
    const orgId = currentOrganisation?.id;
    if (!orgId) return;

    let cancelled = false;

    const loadOrganisation = async () => {
      try {
        const res = await withoutPermissionToast(() =>
          organisationApi.searchOrganisations({
            ids: [orgId],
            pageSize: 1,
            pageIndex: 0,
          }),
        );
        if (cancelled) return;

        const org = (res?.data || [])[0];
        if (!org) return;

        // Module access comes from the licensing snapshot now; the only
        // thing still read off the organisation record here is its time zone.
        setOrganisationTimeZone(org.timeZone || null);
      } catch (err) {
        if (!isForbidden(err)) {
          console.error("Failed to resolve enabled modules:", err);
        }
      }
    };

    loadOrganisation();

    return () => {
      cancelled = true;
    };
  }, [currentOrganisation?.id]);

  const switchOrganisation = useCallback(
    async (organisationId: string, organisationName: string) => {
      setSwitching(true);
      try {
        const result = await organisationApi.switchOrganisation(organisationId);

        localStorage.setItem("accessToken", result.token);
        if (result.refreshToken) {
          localStorage.setItem("refreshToken", result.refreshToken);
        }
        localStorage.setItem("organisation", result.groupId || organisationId);
        localStorage.setItem(
          "companyName",
          result.companyName || organisationName,
        );

        setCurrentOrganisation({
          id: result.groupId || organisationId,
          name: result.companyName || organisationName,
        });

        toast.success(
          t("switchedOrganisation", {
            name: result.companyName || organisationName,
          }),
        );

        // Hard reload rather than resetting Redux slices piecemeal — this
        // app has no active React Query usage despite the provider being
        // mounted, so a full reload is the one guaranteed way to avoid any
        // screen briefly showing data fetched under the previous tenant's
        // token (see the org-switch design notes: stale cross-tenant state
        // is the most common way this kind of feature fails).
        window.location.href = "/";
      } catch (err) {
        console.error("Failed to switch organisation:", err);
        toast.error(t("switchOrganisationFailed"));
        setSwitching(false);
      }
    },
    [t],
  );

  return (
    <OrganisationSwitchContext.Provider
      value={{
        currentOrganisation,
        organisationTimeZone,
        switching,
        switchOrganisation,
      }}
    >
      {children}
    </OrganisationSwitchContext.Provider>
  );
};

export const useOrganisationSwitch = () => {
  const context = useContext(OrganisationSwitchContext);
  if (!context) {
    throw new Error(
      "useOrganisationSwitch must be used inside OrganisationSwitchProvider",
    );
  }
  return context;
};
